import { HttpClient, HttpContext, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';
import {
  AddSafetyRoundItemRequest,
  AssignedSafetyRoundTemplate,
  CompleteSafetyRoundRequest,
  CreateSafetyRoundFindingDeviationRequest,
  GradeSafetyRoundItemRequest,
  MarkNotApplicableRequest,
  SafetyRoundDetail,
  SafetyRoundItem,
  SafetyRoundPermissions,
  SafetyRoundPhoto,
  SafetyRoundSummary,
  StartSafetyRoundRequest,
  UpdateSafetyRoundDetailsRequest,
} from './safety-round.models';

/** A downloaded file and the name the server suggested for it, if it exposed one. */
export interface DownloadedFile {
  blob: Blob;
  fileName: string | null;
}

/**
 * Vernerunder for one business — FlexiSafetyRoundsController.
 *
 * Reads are open to anyone who may open the business; every write is refused
 * by the backend to anyone but an Admin or one of the business's HSE advisors.
 * The route lives on the v1 prefix (`api/kuba/v1/flexi/...`) beside the rest
 * of Flexi, so it is composed from BASE_URL rather than BASE_URL_V2.
 *
 * Errors are left to the global interceptor toast unless a caller says
 * otherwise; the components only reload or re-enable controls on failure.
 */
@Injectable({ providedIn: 'root' })
export class FlexiSafetyRoundService {
  readonly deviationsChanged = new Subject<void>();
  private readonly base = `${environment.BASE_URL}/flexi/safety-rounds`;

  constructor(private http: HttpClient) {}

  /**
   * Whether the caller may start, edit, grade and complete rounds here. The
   * backend decides (Admin or a listed HSE (BHT) advisor only); the
   * pages only hide what it would refuse.
   */
  permissions(businessId: number): Observable<SafetyRoundPermissions> {
    // A failed probe leaves the page read-only rather than broken, so it is
    // answered quietly instead of with an error toast.
    return this.http
      .get<SafetyRoundPermissions | null>(`${this.base}/${businessId}/permissions`, {
        context: new HttpContext().set(SKIP_ERROR_TOAST, true),
      })
      .pipe(
        map(result => ({ CanAuthor: result?.CanAuthor === true })),
        catchError(() => of({ CanAuthor: false })),
      );
  }

  /** Every round of the business, newest RoundDate first. */
  list(businessId: number): Observable<SafetyRoundSummary[]> {
    return this.http
      .get<SafetyRoundSummary[] | null>(`${this.base}/${businessId}`)
      .pipe(map(rows => rows ?? []));
  }

  /**
   * Templates the business may start from. The first call for a business with
   * no assignments also assigns the application's default templates, so this
   * is never empty for long.
   */
  templates(businessId: number): Observable<AssignedSafetyRoundTemplate[]> {
    return this.http
      .get<AssignedSafetyRoundTemplate[] | null>(`${this.base}/${businessId}/templates`)
      .pipe(map(rows => rows ?? []));
  }

  /** Starts a round and returns its id. */
  start(businessId: number, request: StartSafetyRoundRequest): Observable<number> {
    return this.http
      .post<number>(`${this.base}/${businessId}`, request)
      .pipe(map(id => Number(id)));
  }

  get(businessId: number, roundId: number): Observable<SafetyRoundDetail> {
    return this.http.get<SafetyRoundDetail>(`${this.base}/${businessId}/${roundId}`);
  }

  getItem(businessId: number, roundId: number, itemId: number): Observable<SafetyRoundItem> {
    return this.http.get<SafetyRoundItem>(`${this.base}/${businessId}/${roundId}/items/${itemId}`);
  }

  updateDetails(businessId: number, roundId: number, request: UpdateSafetyRoundDetailsRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/${roundId}`, request, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  /** Only administrators can reopen completed rounds. */
  reopen(businessId: number, roundId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${businessId}/${roundId}/reopen`, {});
  }

  /** Soft-deletes a round; completed rounds require an administrator. */
  delete(businessId: number, roundId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${businessId}/${roundId}`);
  }

  /**
   * Saves one checkpoint's grade and comment together. 409 when the round is
   * completed, or when the grade would change while the item has a deviation.
   */
  gradeItem(businessId: number, roundId: number, itemId: number, request: GradeSafetyRoundItemRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${businessId}/${roundId}/items/${itemId}`, request);
  }

  /** Adds a checkpoint to an area during the round and returns its id. */
  addItem(businessId: number, roundId: number, request: AddSafetyRoundItemRequest): Observable<number> {
    return this.http
      .post<number>(`${this.base}/${businessId}/${roundId}/items`, request)
      .pipe(map(id => Number(id)));
  }

  /** Grades every ungraded item 0 — in one area, or the whole round when SectionName is null. */
  markNotApplicable(businessId: number, roundId: number, request: MarkNotApplicableRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/${businessId}/${roundId}/not-applicable`, request);
  }

  /** Registers the finding as a deviation (Avvik) and returns the deviation id. */
  createDeviation(businessId: number, itemId: number, request: CreateSafetyRoundFindingDeviationRequest): Observable<number> {
    return this.http
      .post<number>(`${this.base}/${businessId}/items/${itemId}/deviation`, request)
      .pipe(map(id => Number(id)), tap(() => this.deviationsChanged.next()));
  }

  /**
   * Uploads one photo to a checkpoint (field `photo`). Reports progress
   * events. 400 for an invalid file, 409 when the round is completed or the
   * checkpoint already has 10 photos — shown by the caller, so the global
   * toast is suppressed.
   */
  uploadPhoto(businessId: number, roundId: number, itemId: number, file: File): Observable<HttpEvent<SafetyRoundPhoto>> {
    const form = new FormData();
    form.append('photo', file, file.name);
    return this.http.post<SafetyRoundPhoto>(
      `${this.base}/${businessId}/${roundId}/items/${itemId}/photos`,
      form,
      { reportProgress: true, observe: 'events', context: new HttpContext().set(SKIP_ERROR_TOAST, true) },
    );
  }

  /** 409 when the round is completed. */
  deletePhoto(businessId: number, roundId: number, itemId: number, photoId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${businessId}/${roundId}/items/${itemId}/photos/${photoId}`);
  }

  /** 400 while items are ungraded or a finding has no deviation. */
  complete(businessId: number, roundId: number, request: CompleteSafetyRoundRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/${businessId}/${roundId}/complete`, request);
  }

  /** The round's PDF report, with the server's file name when it is readable. */
  downloadReport(businessId: number, roundId: number): Observable<DownloadedFile> {
    return this.http
      .get(`${this.base}/${businessId}/${roundId}/report`, { observe: 'response', responseType: 'blob' })
      .pipe(map(response => ({ blob: response.body ?? new Blob(), fileName: fileNameOf(response) })));
  }
}

/**
 * The file name from a Content-Disposition header, preferring the RFC 5987
 * `filename*` form. Null when the header is absent or not exposed to scripts
 * (CORS hides it unless the server lists it in Access-Control-Expose-Headers).
 */
export function fileNameOf(response: HttpResponse<unknown>): string | null {
  const header = response.headers.get('Content-Disposition');
  if (!header) {
    return null;
  }
  const encoded = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/** Hands a blob to the browser as a download through a temporary object URL. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
